import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Typography } from '@mui/material';

import { POS_SYSTEMS } from '@/config/station';
import { CommonError, handleError } from '@/utils/errorUtils';

import { prepareData } from '@/components/createStation/helpers';
import { FormikForm } from '@/components/formik/FormikForm';
import { FormikSelect } from '@/components/formik/FormikSelect';
import { FormikSubmit } from '@/components/formik/FormikSubmit';
import { Download } from '@/components/icons/Download';
import { GenericModalFooter } from '@/components/modal/GenericModalFooter';
import { PosInstructions } from '@/components/PosInstructions';
import type { TStepComponentProps } from '@/components/stepper/types';
import { SubmitError } from '@/components/submitError/SubmitError';

import type { TOnSubmit } from '@/types/formTypes';
import type { TCreateStationForm } from '@/types/station';

import { useCreateStation } from '@/mutations/station';
import { stationPosInfoSchema } from '@/validation/station';

import { styles as formStyles } from './Form.styles';

type THandleSubmit = TOnSubmit<TCreateStationForm['posInfo']>;

export const SystemConnection = ({
  data,
  moveBack,
  onSubmit,
}: TStepComponentProps<TCreateStationForm>) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  // W3: creates the station and marks station data old. `mutateAsync` is awaited by the submit
  // handler, so formik keeps `isSubmitting` true (Next disabled) for the whole call: no double create.
  const { mutateAsync: create } = useCreateStation();

  const handleSubmit: THandleSubmit = async (posInfo) => {
    try {
      const form = { ...data, posInfo } as TCreateStationForm;
      const prepared = await prepareData(form);

      const result = await create(prepared);

      await onSubmit({ posInfo, result });
    } catch (err) {
      if (err instanceof CommonError) {
        setError(t(err.messageKey));
      } else {
        setError(`${t('errors.unknown')}\n${t('errors.tryAgain')}`);
        handleError(err, 'UNHANDLED_RESPONSE');
      }
    }
  };

  return (
    <FormikForm
      initialValues={data.posInfo}
      validationSchema={stationPosInfoSchema}
      onSubmit={handleSubmit}
      sx={formStyles.form}
    >
      <SubmitError error={error} dismiss={() => setError(null)} sx={formStyles.errorToast} />

      <Box sx={formStyles.content}>
        <Box sx={formStyles.section}>
          <Typography variant="h6" sx={formStyles.sectionTitle}>
            {t('stationSetup.connectSystem.title')}
          </Typography>

          <FormikSelect
            name="0.system"
            label="stationSetup.connectSystem.posSystem"
            options={POS_SYSTEMS}
            disabled={data.type === 'CHARGING_STATION'}
          />
        </Box>

        <Box sx={formStyles.section}>
          <Typography variant="h6" sx={formStyles.sectionTitle}>
            {t('stationSetup.connectSystem.instructions.title')}
          </Typography>

          <Typography color="text.secondary" marginBottom={3}>
            {t('stationSetup.connectSystem.instructions.details')}
          </Typography>

          <PosInstructions name="0" size="large" startIcon={<Download />} fullWidth>
            {t('stationSetup.connectSystem.instructions.download')}
          </PosInstructions>
        </Box>
      </Box>

      <GenericModalFooter sx={formStyles.footer} squareButtons>
        <Box sx={formStyles.controls}>
          <Button variant="outlined" onClick={moveBack}>
            {t('common.back')}
          </Button>

          <FormikSubmit>{t('common.next')}</FormikSubmit>
        </Box>
      </GenericModalFooter>
    </FormikForm>
  );
};
